import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	AttachmentList,
	Button,
	Dialog,
	FileSelector,
	Form,
	FormItem,
	Icon,
	TextArea,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useIsTouchDevice,
	useUnmountedCancellation
} from "client/Hooks";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { getExtensionAndType } from "client_legacy/Documents/fileExtensions";
import { ConnectWiseEvent, publish } from "client_legacy/Workspace/LiveInformation/ConnectWise/events";
import {
	CreateTicketModel,
	ServiceTicketListItem,
	addAttachment,
	createTicket,
	deleteTicket
} from "client/Api/Workspace/LiveInformation/ConnectWise/connectWiseLiveInformation";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { generateTrackingId, isNil } from "client/Misc/utils";

export type CreateTicketDialogProps = {
	onClose(): void;
};

export const CreateTicketDialog = (props: CreateTicketDialogProps): ReactElement => {
	const { onClose } = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
	const [formData, setFormData] = useState<CreateTicketModel>({
		description: "",
		title: ""
	});

	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<CreateTicketModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const uploadAttachmentHandler = useCallback(
		async (ticket: ServiceTicketListItem | undefined): Promise<void> => {
			if (isNil(ticket)) {
				return;
			}

			try {
				const promises: Promise<void>[] = attachedFiles.map(x => addAttachment(
					{
						ticketId: ticket.id,
						file: x
					},
					globalCancellation
				));

				await Promise.allSettled(promises);
				return;
			} catch(e) {
				try {
					await deleteTicket(ticket.id);
				} catch {
					if (!globalCancellation.aborted) {
						alertManager.showError(
							localize("_OneOrMoreAttachmentsFailedToUploadPleaseCheckYourTicket")
						);
						publish(ConnectWiseEvent.Created, ticket);
						onClose();
					}

					throw new Error(`failed to delete ticket ${ticket.id}`);
				}

				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(
					localize("_FailedToSubmitTheTicket")
				);

				setIsBusy(false);

				throw new Error(`failed to submit ticket ${ticket.id}`);
			}

		},
		[
			alertManager,
			attachedFiles,
			globalCancellation,
			onClose
		]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			let ticket: ServiceTicketListItem;
			try {
				setIsBusy(true);

				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate();

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				ticket = await createTicket(
					formHandler.value,
					globalCancellation
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (
					!formHandler.validation
						.tryProcessServerError(e)
				) {
					alertManager.showError(
						localize("_FailedToSubmitTheTicket")
					);
				}

				setIsBusy(false);
				return;
			}

			try {
				await uploadAttachmentHandler(ticket);
			} catch (e) {
				return;
			}

			alertManager.showSuccess(
				localize("_SuccessfullySubmittedTheTicket")
			);
			publish(ConnectWiseEvent.Created, ticket);
			onClose();
		},
		[
			alertManager,
			onClose,
			formHandler.validation,
			formHandler.value,
			globalCancellation,
			uploadAttachmentHandler
		]
	);

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="create-connect-wise-ticket-dialog"
		>
			<Dialog.Header
				title={localize("_NewTicket")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem
						label={localize("_Title")}
						isRequired
					>
						<TextInput
							{...formHandler.for("title")
								.attachToTextInput()
							}
							autoFocus="focus"
						/>

						<ValidationError
							{...formHandler.for("title")
								.attachToValidationError()
							}
						/>
					</FormItem>
					<FormItem
						label={localize("_Description")}
						isRequired
					>
						<TextArea
							{...formHandler.for("description")
								.attachToTextArea()
							}
							rows={6}
						/>
						<ValidationError
							{...formHandler.for("description")
								.attachToValidationError()
							}
						/>
					</FormItem>
					<FormItem>
						<Attachments
							items={attachedFiles}
							onChange={setAttachedFiles}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Submit")}
						onClick={submitHandler}
						trackingId="submit"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
CreateTicketDialog.displayName="CreateTicketDialog";

type AttachmentsProps = {
	items: File[];
	onChange(files: File[]): void;
};

const Attachments = (props: AttachmentsProps): ReactElement => {
	const {
		items,
		onChange
	} = props;

	const isTouch: boolean = useIsTouchDevice();

	const addAttachmentHandler = useCallback(
		(files: File[]): void => {
			onChange([
				...items,
				...files
			]);
		},
		[items, onChange]
	);

	const removeAttachmentHandler = useCallback(
		(fileIndex: number): void => {
			const copy: File[] = [...items];
			copy.splice(fileIndex, 1);

			onChange(copy);
		},
		[items, onChange]
	);

	const actions = useMemo(
		(): readonly Action<ActionContext>[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "remove",
					title: localize("_Remove"),
					canExecute: (): boolean => true,
					execute: (c): void => removeAttachmentHandler(c.attachmentIndex)
				}
			];
		},
		[removeAttachmentHandler]
	);

	return (
		<div>
			<FileSelector
				multiple
				onChange={addAttachmentHandler}
			>
				{(onClick) => (
					<Button
						kind="secondary"
						text={localize("_AddAttachment")}
						onClick={onClick}
						trackingId="add-attachment-button"
					/>
				)}
			</FileSelector>
			{items.length > 0 && (
				<AttachmentList trackingId="attachments-list">
					{items.map((x: File, index) => (
						<AttachmentList.Item
							trackingId={generateTrackingId("item", `|${index}`)}
							key={x.name}
							actionContext={{ attachmentIndex: index }}
							actions={actions}
							actionsTitle={x.name}
							isTouch={isTouch}
						>
							<AttachmentList.Item.Content
								name={x.name}
								thumbnail={
									<Icon
										value={{
											kind: "svg",
											name: getExtensionAndType(x.name).type
										}}
									/>
								}
								actionContext={{attachmentIndex: index}}
								actions={actions}
							/>
						</AttachmentList.Item>
					))}
				</AttachmentList>
			)}
		</div>
	);
};

Attachments.displayName = "ConnectWiseDialog.Attachments";

type ActionContext = {
	attachmentIndex: number;
};

export const validationConfiguration: IValidationConfiguration<CreateTicketModel> =
	createValidationConfiguration<CreateTicketModel>();

validationConfiguration.forScalar("title")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forScalar("description")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export function useCreateTicketDialog(): [
	element: ReactElement | null,
	open: () => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<{}, void>(
		(_, onClose) => (
			<CreateTicketDialog
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (): Promise<void> => {
			await originalOpen({});
		},
		[originalOpen]
	);

	return [element, open];
}
